import { useUser } from "@/hooks/use-user";
import { useLocation } from "wouter";
import { useEffect } from "react";
import Layout from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatsCards } from "@/components/stats-cards";
import { Spinner } from "@/components/ui/spinner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Users,
  FolderKanban,
  UserCheck,
  Building2,
  Award,
  Clock,
  Briefcase,
  Target,
} from "lucide-react";
import { useAnalytics } from "@/hooks/use-analytics";

const COLORS = ["#2563eb", "#16a34a", "#eab308", "#dc2626", "#9333ea"];

export default function Analytics() {
  const { user, isLoading: userLoading } = useUser();
  const [, setLocation] = useLocation();
  const { summary, monthlyStats, vendorPerformance, isLoading: dataLoading, error } = useAnalytics();

  useEffect(() => {
    if (!userLoading && !user) {
      setLocation("/login");
    }
  }, [user, userLoading, setLocation]);

  if (userLoading || dataLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Spinner className="h-8 w-8" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-screen">
          <p className="text-red-500">Error loading analytics data</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      </Layout>
    );
  }

  const stats = summary ? [
    {
      title: "Total Roles",
      value: summary.roles.total,
      description: `${summary.roles.active} Active`,
      icon: FolderKanban,
      color: "text-blue-600",
    },
    {
      title: "Role Success Rate",
      value: `${Math.round((summary.roles.completed / summary.roles.total) * 100)}%`,
      description: `${summary.roles.completed} Completed`,
      icon: Target,
      color: "text-green-600",
    },
    {
      title: "Avg Role Duration",
      value: `${Math.round(summary.roles.avgDuration || 0)} days`,
      description: "Time to Complete",
      icon: Clock,
      color: "text-yellow-600",
    },
    {
      title: "Total Candidates",
      value: summary.candidates.total,
      description: `${summary.candidates.hired} Hired`,
      icon: UserCheck,
      color: "text-purple-600",
    },
    {
      title: "Hiring Success Rate",
      value: `${Math.round((summary.candidates.hired / summary.candidates.total) * 100)}%`,
      description: "Placement Rate",
      icon: Award,
      color: "text-red-600",
    },
    {
      title: "Avg Experience",
      value: `${Math.round(summary.candidates.avgExperience || 0)} years`,
      description: "Candidate Experience",
      icon: Briefcase,
      color: "text-indigo-600",
    },
    {
      title: "Active Vendors",
      value: summary.vendors.active,
      description: `${summary.vendors.total} Total`,
      icon: Building2,
      color: "text-cyan-600",
    },
    {
      title: "Roles per Vendor",
      value: summary.vendors.avgRolesPerVendor?.toFixed(1) || "0",
      description: "Average roles",
      icon: Users,
      color: "text-pink-600",
    },
  ] : [];

  const roleStatusData = summary ? [
    { name: "Active", value: summary.roles.active },
    { name: "Completed", value: summary.roles.completed },
    { name: "On Hold", value: summary.roles.onHold },
  ] : []; 

  const candidateStatusData = summary ? [
    { name: "New", value: summary.candidates.new },
    { name: "Screening", value: summary.candidates.screening },
    { name: "Interview", value: summary.candidates.interview },
    { name: "Hired", value: summary.candidates.hired },
    { name: "Rejected", value: summary.candidates.rejected },
  ] : [];

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            Analytics Dashboard
          </h2>
          <p className="text-muted-foreground">
            Comprehensive metrics and performance indicators
          </p>
        </div>

        {dataLoading ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <StatsCards stats={stats} />
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {/* Monthly Activity Trends */}
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle>Monthly Activity Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {dataLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Spinner className="h-8 w-8" />
                  </div>
                ) : (
                  monthlyStats && (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={monthlyStats.roles.map((item: any) => ({
                          month: item.month,
                          "New Roles": item.count,
                          "Completed Roles": item.completed,
                          "New Candidates":
                            monthlyStats.candidates.find(
                              (c: any) => c.month === item.month,
                            )?.count || 0,
                          "Hired Candidates":
                            monthlyStats.candidates.find(
                              (c: any) => c.month === item.month,
                            )?.hired || 0,
                          "New Vendors":
                            monthlyStats.vendors.find(
                              (v: any) => v.month === item.month,
                            )?.count || 0,
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="New Roles"
                          stroke="#2563eb"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="Completed Roles"
                          stroke="#16a34a"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="New Candidates"
                          stroke="#9333ea"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="Hired Candidates"
                          stroke="#eab308"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="New Vendors"
                          stroke="#dc2626"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )
                )}
              </div>
            </CardContent>
          </Card>

          {/* Role Status Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Role Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {dataLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Spinner className="h-8 w-8" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={roleStatusData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      >
                        {roleStatusData.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Candidate Status Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Candidate Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {dataLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Spinner className="h-8 w-8" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={candidateStatusData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      >
                        {candidateStatusData.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Required Skills */}
          <Card>
            <CardHeader>
              <CardTitle>Most Required Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {dataLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Spinner className="h-8 w-8" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={summary?.topSkills}
                      layout="vertical"
                      margin={{ left: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="skill" width={100} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2563eb" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Candidate Skills */}
          <Card>
            <CardHeader>
              <CardTitle>Most Common Candidate Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {dataLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Spinner className="h-8 w-8" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={summary?.topCandidateSkills}
                      layout="vertical"
                      margin={{ left: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="skill" width={100} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#16a34a" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vendor Performance */}
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle>Vendor Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {dataLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Spinner className="h-8 w-8" />
                  </div>
                ) : (
                  vendorPerformance && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={vendorPerformance} margin={{ left: 100 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="vendorName" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar
                          name="Total Roles"
                          dataKey="totalRoles"
                          fill="#2563eb"
                        />
                        <Bar
                          name="Completed Roles"
                          dataKey="completedRoles"
                          fill="#16a34a"
                        />
                        <Bar
                          name="Active Roles"
                          dataKey="activeRoles"
                          fill="#eab308"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
